import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getSlaDashboard } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, Timer, Anchor, Ship, Building2, Route } from 'lucide-react';

function StatCard({ item, color }) {
  if (!item || item.count === 0) return null;
  return (
    <div className="flex items-center justify-between p-3 bg-muted/20 border border-border/50 rounded-lg">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.name}</p>
        <p className="text-xs text-muted-foreground">{item.count} contenedores</p>
      </div>
      <div className="flex items-center gap-3 ml-3">
        <div className="text-right">
          <p className={`text-lg font-heading ${color}`}>{item.avg}d</p>
          <p className="text-[10px] text-muted-foreground">promedio</p>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <p>{item.min}d min</p>
          <p>{item.max}d max</p>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, items, color, emptyText }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
        <Icon size={16} className={color} />
        {title}
      </h3>
      {items.length > 0 ? (
        <div className="space-y-2">
          {items.map((item, i) => (
            <StatCard key={i} item={item} color={color} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground py-4 text-center">{emptyText}</p>
      )}
    </div>
  );
}

export default function SlaPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getSlaDashboard();
      setData(res.data);
    } catch {
      toast.error('Error al cargar datos de SLA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="sla-page">
        <div>
          <h1 className="text-2xl font-heading text-foreground flex items-center gap-2">
            <Timer className="text-primary" size={24} />
            Dashboard SLA / Rendimiento
          </h1>
          <p className="text-muted-foreground mt-1">Tiempos promedio de proceso desde arribo hasta entrega (dias)</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : data ? (
          <>
            {/* Overall KPI */}
            {data.overall && data.overall.count > 0 && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Promedio General</p>
                    <p className="text-4xl font-heading text-primary">{data.overall.avg} dias</p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>Min: {data.overall.min}d | Max: {data.overall.max}d</p>
                    <p>{data.overall.count} contenedores analizados</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard
                title="Por Terminal"
                icon={Anchor}
                items={data.by_terminal || []}
                color="text-orange-400"
                emptyText="Sin datos por terminal"
              />
              <SectionCard
                title="Por Naviera"
                icon={Ship}
                items={data.by_carrier || []}
                color="text-blue-400"
                emptyText="Sin datos por naviera"
              />
              <SectionCard
                title="Por Importadora"
                icon={Building2}
                items={data.by_importer || []}
                color="text-emerald-400"
                emptyText="Sin datos por importadora"
              />
              <SectionCard
                title="Por Ruta"
                icon={Route}
                items={data.by_route || []}
                color="text-violet-400"
                emptyText="Sin datos por ruta"
              />
            </div>
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
